// ToolCallRow organism (Layer 5 §3.9) — moved from founder/FounderToolRow.tsx.
// Canonical name is ToolCallRow; FounderToolRow kept as re-export alias.
//
// Sprint 6 (Phase 1) — Founder-mode tool row.
// Sprint ai-elements-rendering (Phase 2, T+X) — visual shell restyled to the
// AI Elements ChainOfThoughtStep language (ADR D-026): icon rail + connector
// line + muted completed state, replacing the bordered `card-tool` chip.
// Every pre-existing behaviour is preserved verbatim: headline source,
// D-msg-8 per-row override, md-doc split hit-areas, waiting ticker,
// developer reveal, all testids.
//
// Single shared component for every tool call rendered in founder-mode. One
// line: icon + headline + status glyph + per-message ▽ expander. Headline is
// the model's `tool_use.input.description` when present, else a per-tool
// fallback (verb-map.ts).
//
// Per-message ▽ behavior:
//   • Closed by default when global `developerMode` is false (founder default).
//   • Open by default when global `developerMode` is true (developer override).
//   • Click toggles LOCAL state — does NOT mutate the global setting. This
//     keeps the per-message escape hatch independent of "I want diff for
//     every tool from now on".

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from '../atoms/Icon';
import { useTranslation } from '../../i18n';
import type { Message } from '../../types';
import { toFounderRow, type FounderRow, type RowLivenessCtx } from '../../lib/tool-rendering/founder-layer';
import { useSettingsStore } from '../../stores/settings.store';
import { useDocViewer } from '../../hooks/useDocViewer';
import { useElapsed } from '../../hooks/useElapsed';
import {
  formatActionDuration,
  formatClockTime,
  formatElapsed,
} from '../../lib/tool-rendering/format-elapsed';

// Sprint monitor-tool-live-timer — inline elapsed counter for tool calls
// still in flight (no paired `tool_result` yet). Visible only after 2 s
// (D4) so fast tools never flash a 0:00. Mirrors Cursor's "Waiting Xs"
// affordance for the `Monitor` tool; the threshold means it kicks in
// automatically for any genuinely long-running tool.
const WAITING_THRESHOLD_MS = 2_000;

function WaitingTicker({ startedAtMs }: { startedAtMs: number }) {
  const { t } = useTranslation();
  const elapsedMs = useElapsed(startedAtMs);
  if (elapsedMs < WAITING_THRESHOLD_MS) return null;
  return (
    <span
      className="text-caption text-muted-foreground shrink-0 tabular-nums"
      data-testid="founder-tool-waiting"
    >
      {formatElapsed(elapsedMs, t)}
    </span>
  );
}

// Sprint md-viewer-coverage-gaps (D-003) — when a `Read` tool call targets
// a `.md` file, the headline area opens the existing DocViewerSheet on
// click instead of toggling the developer expander. The chevron stays as
// a separate hit-area, so the per-message dev override (D-004) is
// preserved. Non-`.md` Read calls (and every other tool) keep the
// original whole-row-toggles-expander behaviour.
function readsMdFile(row: FounderRow): string | null {
  if (row.rawTool !== 'Read') return null;
  const fp = row.inputs?.['file_path'];
  if (typeof fp !== 'string' || fp.length === 0) return null;
  if (!fp.toLowerCase().endsWith('.md')) return null;
  return fp;
}

export type ToolCallRowProps = FounderToolRowProps;

export const ToolCallRow = FounderToolRow;

export interface FounderToolRowProps {
  toolUseMessage: Message;
  toolResultMessage?: Message;
  // Sprint orphan-tool-rows-honest (D013-EC-147) — plausibly-live context
  // for the unpaired-row status derivation. Threaded from
  // ConversationList/SubagentPanel via MessageBubble; omitted = legacy
  // behavior (unpaired renders running).
  livenessCtx?: RowLivenessCtx;
  // When provided, replaces the default `<DeveloperReveal>` block. Used by
  // AggregationCard and (Phase 8) by the tool-specific dev-mode renderers.
  developerSlot?: ReactNode;
  // Test seam — when set, overrides the row's pre-computed FounderRow. Used
  // by AggregationCard so the aggregated row reuses the same visual shell.
  rowOverride?: FounderRow;
  // When true, the per-message expander is hidden (e.g. inside an aggregated
  // card where each child has its own expander already).
  hideExpander?: boolean;
}

export function FounderToolRow({
  toolUseMessage,
  toolResultMessage,
  livenessCtx,
  developerSlot,
  rowOverride,
  hideExpander,
}: FounderToolRowProps) {
  const { t } = useTranslation();
  const developerMode = useSettingsStore((s) => s.developerMode);
  const row = rowOverride ?? toFounderRow(toolUseMessage, toolResultMessage, livenessCtx);
  // Sprint messages-section-fixes (D-msg-8) — local override now
  // survives global-toggle changes. Pre-fix, the local `open` state
  // was reset to the global `developerMode` on every flip; that
  // erased any per-message decision the founder had made (e.g.
  // "expand THIS one to debug"). The fix: track whether the user has
  // touched the local toggle. While untouched, the row mirrors the
  // global state on every render. Once touched, the local choice
  // wins and survives any number of global flips.
  const [open, setOpen] = useState<boolean>(developerMode);
  const userTouchedRef = useRef<boolean>(false);
  useEffect(() => {
    if (userTouchedRef.current) return;
    setOpen(developerMode);
  }, [developerMode]);
  const handleToggle = () => {
    userTouchedRef.current = true;
    setOpen((v) => !v);
  };

  const isError = row.status === 'error';

  // Step-language tones (chain-of-thought.tsx statusStyles): a finished
  // step reads muted, the active one reads foreground, an error keeps
  // the status color. Same mapping as the subagent tree (Phase 1).
  const headlineColor = isError
    ? 'text-status-error'
    : row.status === 'running'
      ? 'text-foreground'
      : 'text-muted-foreground';
  const iconColor = isError ? 'text-status-error' : 'text-muted-foreground';

  const mdFilePath = readsMdFile(row);
  const { openDoc } = useDocViewer();
  const openInViewer = mdFilePath !== null && !hideExpander;

  // Anchor for the in-flight elapsed ticker. `Message.timestamp` is ISO
  // 8601 wall-clock — survives reload. A malformed / missing timestamp
  // falls back to "now" so the ticker degrades gracefully instead of
  // rendering NaN (MT-1 in the spec).
  const startedAtMs = (() => {
    const ts = Date.parse(toolUseMessage.timestamp);
    return Number.isFinite(ts) ? ts : Date.now();
  })();
  const showTicker = row.status === 'running';

  // Sprint compact-action-rows (D5/D6) — a finished action states when it
  // happened and how long it took. The live ticker already owns the
  // running case, so the duration only renders once the row settles; the
  // clock renders either way. Unparseable / missing timestamps render
  // nothing rather than `Invalid Date` or a misleading `0s`.
  const settledAtMs = (() => {
    if (!toolResultMessage) return null;
    const ts = Date.parse(toolResultMessage.timestamp);
    return Number.isFinite(ts) ? ts : null;
  })();
  const startedAtMsOrNull = (() => {
    const ts = Date.parse(toolUseMessage.timestamp);
    return Number.isFinite(ts) ? ts : null;
  })();
  // D5 says the row's clock comes from the message's OWN timestamp, i.e.
  // when the action STARTED. Gate 2 caught the first version preferring
  // the result's timestamp, which made a long action's displayed time
  // jump forward the moment it finished. Last-activity semantics belong
  // to group rows, where the group has no single start to show.
  const clock = formatClockTime(startedAtMsOrNull);
  const settledDuration =
    !showTicker && settledAtMs !== null && startedAtMsOrNull !== null
      ? formatActionDuration(settledAtMs - startedAtMsOrNull, t)
      : null;
  const meta = [clock, settledDuration].filter(Boolean).join(' · ');
  const metaNode = meta ? (
    <span
      className="shrink-0 text-caption text-muted-foreground/70 tabular-nums"
      data-testid="founder-tool-meta"
    >
      {meta}
    </span>
  ) : null;

  const chevron = (openState: boolean) =>
    openState ? <Icon name="ChevronDown" size={12} /> : <Icon name="ChevronRight" size={12} />;

  return (
    <div
      className="fade-in-0 slide-in-from-top-2 animate-in flex gap-2 text-body"
      data-testid="founder-tool-row"
      data-tool={row.rawTool}
      data-status={row.status}
    >
      <div className="relative mt-1 shrink-0">
        <Icon name={row.iconName} className={iconColor} size={14} aria-hidden="true" />
        <div className="-mx-px absolute top-6 bottom-0 left-1/2 w-px bg-border" />
      </div>
      <div className="flex-1 min-w-0 space-y-1 overflow-hidden">
        {openInViewer ? (
          <div
            className="flex w-full items-center gap-2 rounded-md transition-colors duration-100"
            data-testid="founder-tool-header-row"
          >
            <button
              type="button"
              onClick={() => openDoc(mdFilePath)}
              className="flex flex-1 min-w-0 items-center gap-2 py-0.5 text-left rounded-md hover:bg-accent focus:outline-none focus:ring-1 focus:ring-border cursor-pointer"
              data-testid="founder-tool-header"
              data-md-doc-path={mdFilePath}
              aria-label={`Open ${mdFilePath}`}
            >
              <span className={`flex-1 truncate text-body ${headlineColor}`}>
                {row.headline}
              </span>
              {metaNode}
              {showTicker && <WaitingTicker startedAtMs={startedAtMs} />}
              <StatusGlyph status={row.status} />
            </button>
            <button
              type="button"
              onClick={handleToggle}
              aria-expanded={open}
              aria-label={open ? 'Hide details' : 'Show details'}
              className="text-muted-foreground/45 shrink-0 rounded-md hover:bg-accent focus:outline-none focus:ring-1 focus:ring-border p-0.5"
              data-testid="founder-tool-expander"
            >
              {chevron(open)}
            </button>
          </div>
        ) : (
          <button
            type="button"
            disabled={hideExpander}
            onClick={() => !hideExpander && handleToggle()}
            aria-expanded={open}
            className={`flex w-full items-center gap-2 py-0.5 rounded-md text-left transition-colors duration-100 ${
              hideExpander
                ? 'cursor-default'
                : 'hover:bg-accent focus:outline-none focus:ring-1 focus:ring-border'
            }`}
            data-testid="founder-tool-header"
          >
            <span className={`flex-1 truncate text-body ${headlineColor}`}>
              {row.headline}
            </span>
            {metaNode}
            {showTicker && <WaitingTicker startedAtMs={startedAtMs} />}
            <StatusGlyph status={row.status} />
            {!hideExpander && (
              <span className="text-muted-foreground/45 shrink-0" aria-hidden="true">
                {chevron(open)}
              </span>
            )}
          </button>
        )}
        {open && !hideExpander && (
          <div className="pb-1" data-testid="founder-tool-developer">
            {developerSlot ?? <DefaultDeveloperReveal row={row} />}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusGlyph({ status }: { status: FounderRow['status'] }) {
  const { t } = useTranslation();
  if (status === 'running') {
    return (
      <Icon
        name="Loader2"
        className="shrink-0 text-muted-foreground animate-spin"
        size={14}
        aria-label={t('aggregation.running')}
      />
    );
  }
  if (status === 'error') {
    return (
      <Icon
        name="X"
        className="shrink-0 text-status-error"
        size={14}
        aria-label={t('aggregation.errored', { count: 1 })}
      />
    );
  }
  // Sprint orphan-tool-rows-honest (D013-EC-147) — the tool never got its
  // result and the agent is no longer working: quiet muted glyph, no
  // spinner, no ticking timer. The crash disclosure + composer-as-resume
  // (D-008) remain the action surface.
  if (status === 'interrupted') {
    return (
      <Icon
        name="PauseCircle"
        className="shrink-0 text-muted-foreground"
        size={14}
        aria-label={t('toolCall.interrupted')}
      />
    );
  }
  return (
    <Icon
      name="Check"
      className="shrink-0 text-status-success"
      size={14}
      aria-label={t('aggregation.completed')}
    />
  );
}

// The default developer-mode reveal: raw inputs + result as JSON. Phase 8
// replaces this with tool-specific renderers (BashBlock, EditDiffBlock,
// etc.) for the highest-value tools; this stays as the safety net for any
// tool that doesn't have a specialized renderer.
function DefaultDeveloperReveal({ row }: { row: FounderRow }) {
  const { t } = useTranslation();
  const inputJson = (() => {
    try {
      return JSON.stringify(row.inputs, null, 2);
    } catch {
      return String(row.inputs);
    }
  })();
  const resultText = (() => {
    if (row.result === undefined) return null;
    if (row.result === null) return t('toolCall.empty');
    if (typeof row.result === 'string') return row.result;
    try {
      return JSON.stringify(row.result, null, 2);
    } catch {
      return String(row.result);
    }
  })();
  return (
    <div className="space-y-2 text-xs"> {/* design-discipline: kept as text-xs — developer-mode reveal */}
      <div>
        <div className="text-2xs uppercase tracking-wide text-muted-foreground/45 mb-1"> {/* design-discipline: kept as text-2xs — developer-mode section header */}
          {t('toolCall.inputs')}
        </div>
        <pre
          className="bg-background border border-border/50 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words text-muted-foreground"
          data-testid="founder-tool-inputs"
        >
          {inputJson}
        </pre>
      </div>
      {resultText !== null && (
        <div>
          <div className="text-2xs uppercase tracking-wide text-muted-foreground/45 mb-1"> {/* design-discipline: kept as text-2xs — developer-mode section header */}
            {row.isError ? t('toolCall.errorLabel') : t('toolCall.result')}
          </div>
          <pre
            className={`border rounded p-2 overflow-x-auto whitespace-pre-wrap break-words ${
              row.isError
                ? 'bg-background border-status-error/40 text-status-error'
                : 'bg-background border-border/50 text-muted-foreground'
            }`}
            data-testid="founder-tool-result"
          >
            {resultText}
          </pre>
        </div>
      )}
    </div>
  );
}
